/**
 * @fileoverview Department detail page for viewing and managing a single system-web department.
 * @module system-web/pages/DepartmentDetailPage
 *
 * Responsibilities:
 * - Load a department from the local-first tenant service and render the slide-style detail view.
 * - Coordinate department lifecycle actions such as trial extension and activation through service-backed handlers.
 *
 * Dependencies:
 * - tenantService (all tenant lifecycle actions centralized here)
 * - DepartmentPanel
 *
 * Architecture:
 * - Local-first. Reads come from Dexie-backed services and trial lifecycle actions use shared service orchestration.
 *
 * Notes:
 * - This page should not implement ad hoc trial lifecycle mutations that diverge from TenantsPage.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import DefensibleMiniLoader from '../components/DefensibleMiniLoader';
import { useParams, useNavigate } from 'react-router-dom';
import { useBreadcrumbs } from '../contexts/BreadcrumbContext';
import { ArrowLeft, Building2, AlertTriangle, Pencil, ExternalLink, Smartphone } from 'lucide-react';
import { useTheme } from '@ui-admin/contexts/ThemeContext';
import { getTenant, upsertTenant, convertTenantTrialToActive, extendTenantTrialPeriod, markTenantTrial, cleanupTenantTrial, impersonateTenant, mobileImpersonateTenant } from '../services/tenantService';
import DepartmentPanel, { type PanelTab } from './DepartmentPanel';
import ConfirmActionModal from '../components/ui/ConfirmActionModal';
import EditDepartmentModal from './EditDepartmentModal';
import { openAsyncUrlWithPopupFallback } from '../utils/browserNavigation';
import { applyJurisdictionToAddress } from '../utils/jurisdictionFips';

/**
 * @localFirst
 *
 * Source of Truth:
 * - local
 *
 * Write Flow:
 * - Department state is read from the Dexie-backed tenant service and updated through optimistic local-first service helpers.
 *
 * Sync Strategy:
 * - Shared lifecycle services reconcile the authoritative server record back into Dexie.
 *
 * Conflict Resolution:
 * - Server lifecycle responses win after optimistic local updates.
 */

/**
 * @errorHandling
 *
 * Strategy:
 * - Capture page-level action failures and display them in the inline error banner without leaving the detail page.
 *
 * User Impact:
 * - Operators can correct or retry actions while staying on the same department record.
 *
 * Logging:
 * - REQUIRED
 */

const TIER_COLORS: Record<string, string> = {
  professional: 'bg-blue-100 text-blue-700',
  advanced: 'bg-purple-100 text-purple-700',
  premium: 'bg-amber-100 text-amber-700',
};

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  suspended: 'bg-red-100 text-red-700',
  demo: 'bg-yellow-100 text-yellow-700',
  expired: 'bg-gray-100 text-gray-600',
};

/**
 * @component DepartmentDetailPage
 *
 * Purpose:
 * - Render a single department detail experience with inline editing and trial lifecycle actions.
 *
 * Props:
 * - None. Routing parameters provide the department id.
 *
 * Behavior:
 * - Loads the tenant locally on mount and keeps inline edits optimistic.
 * - Uses shared local-first services to extend trials and activate departments.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - Must not perform direct component-level tenant trial mutations that bypass the shared service layer.
 *
 * Performance:
 * - Caches the current tenant in a ref to support optimistic inline edits without reloading the page.
 */
export default function DepartmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { theme, themeId } = useTheme();
  const { setBreadcrumbLabel } = useBreadcrumbs();

  const [tenant, setTenant] = useState<any | null>(null);
  const tenantRef = useRef<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState<PanelTab>('overview');
  const [actionError, setActionError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [enteringDept, setEnteringDept] = useState(false);
  const [enteringMobile, setEnteringMobile] = useState(false);

  // Trial modals
  const [extendTarget, setExtendTarget] = useState<{ id: string; name: string } | null>(null);
  const [extendDays, setExtendDays] = useState(14);
  const [extendReason, setExtendReason] = useState('');
  const [convertTarget, setConvertTarget] = useState<{ id: string; name: string } | null>(null);
  const [cleanupTarget, setCleanupTarget] = useState<{ id: string; name: string } | null>(null);
  const [markTrialTarget, setMarkTrialTarget] = useState<{ id: string; name: string } | null>(null);
  const [markTrialDays, setMarkTrialDays] = useState(30);
  const [markTrialReason, setMarkTrialReason] = useState('');

  useEffect(() => {
    tenantRef.current = tenant;
  }, [tenant]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getTenant(id)
      .then((t) => {
        if (!t) { setNotFound(true); return; }
        setTenant(t);
        if (t.name) setBreadcrumbLabel(`/tenants/${id}`, t.name);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id, setBreadcrumbLabel]);

  const handleSave = useCallback(async (fieldKey: string, value: unknown) => {
    if (!tenantRef.current) return;

    function deepSet(obj: any, path: string, val: unknown): any {
      const keys = path.split('.');
      if (keys.length === 1) return { ...obj, [keys[0]]: val };
      const key = keys[0];
      return { ...obj, [key]: deepSet(obj[key] ?? {}, keys.slice(1).join('.'), val) };
    }

    let saveValue = value;
    if (fieldKey === 'branding.backgroundOpacity' && typeof value === 'number') {
      saveValue = value / 100;
    }

    const prev = tenantRef.current;
    let updated = deepSet({ ...prev }, fieldKey, saveValue);

    // When the jurisdiction object is saved (State/County lookup selectors),
    // derive address.state and address.county from the jurisdiction so the
    // Address section stays consistent with the single source of truth and the
    // geo-cache parcel matcher has the county name without a duplicate field.
    if (fieldKey === 'jurisdiction' && saveValue && typeof saveValue === 'object') {
      updated = applyJurisdictionToAddress(updated, saveValue as Record<string, any>);
    }

    tenantRef.current = updated;
    setTenant(updated);

    try {
      await upsertTenant(updated);
    } catch (err: any) {
      tenantRef.current = prev;
      setTenant(prev);
      console.error('Failed to save field', fieldKey, err);
    }
  }, []);

  const handleExtend = async () => {
    if (!extendTarget) return;
    try {
      const updated = await extendTenantTrialPeriod(extendTarget.id, extendDays, extendReason);
      setExtendTarget(null);
      setExtendDays(14);
      setExtendReason('');
      if (updated) setTenant(updated);
    } catch (err: any) {
      setActionError(err.message || 'Failed to extend trial');
    }
  };

  const handleConvert = async () => {
    if (!convertTarget) return;
    try {
      const updated = await convertTenantTrialToActive(convertTarget.id);
      setConvertTarget(null);
      if (updated) setTenant(updated);
    } catch (err: any) {
      setActionError(err.message || 'Failed to convert trial');
      setConvertTarget(null);
    }
  };

  const handleMarkAsTrial = async () => {
    if (!markTrialTarget) return;
    try {
      await markTenantTrial(markTrialTarget.id, markTrialDays, markTrialReason);
      setMarkTrialTarget(null);
      setMarkTrialDays(30);
      setMarkTrialReason('');
      const updated = await getTenant(id!);
      if (updated) setTenant(updated);
    } catch (err: any) {
      setActionError(err.message || 'Failed to mark as trial');
      setMarkTrialTarget(null);
    }
  };

  const handleEnterDepartment = async () => {
    if (!tenant) return;
    setEnteringDept(true);
    try {
      await openAsyncUrlWithPopupFallback(
        async () => {
          const res = await impersonateTenant(tenant._id, themeId);
          return res.redirectUrl;
        },
        {
          blockedMessage: 'Safari or your browser blocked opening the tenant in a new tab. Select OK to continue in this tab, or Cancel to stay on the department page.',
        },
      );
    } catch (err: any) {
      setActionError(err.message || 'Failed to generate entry token');
    } finally {
      setEnteringDept(false);
    }
  };

  const handleEnterMobile = async () => {
    if (!tenant) return;
    setEnteringMobile(true);
    try {
      await openAsyncUrlWithPopupFallback(
        async () => {
          const res = await mobileImpersonateTenant(tenant._id, themeId);
          return res.redirectUrl;
        },
        {
          blockedMessage: 'Safari or your browser blocked opening the mobile app in a new tab. Select OK to continue in this tab, or Cancel to stay on the department page.',
        },
      );
    } catch (err: any) {
      setActionError(err.message || 'Failed to generate mobile entry token');
    } finally {
      setEnteringMobile(false);
    }
  };

  const handleCleanup = async () => {
    if (!cleanupTarget) return;
    try {
      await cleanupTenantTrial(cleanupTarget.id);
      setCleanupTarget(null);
      const updated = await getTenant(id!);
      if (updated) setTenant(updated);
    } catch (err: any) {
      setActionError(err.message || 'Failed to clean up trial');
      setCleanupTarget(null);
    }
  };

  if (loading) {
    return (
      <DefensibleMiniLoader label="Loading department…" />
    );
  }

  if (notFound || !tenant) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <Building2 className="h-12 w-12 text-gray-300" />
        <p className="text-gray-500">Department not found</p>
        <button
          onClick={() => navigate('/tenants')}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Departments
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" style={{ background: theme.pageBg }}>
      {/* Error banner */}
      {actionError && (
        <div className="mx-6 mt-3 flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 shrink-0">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span className="flex-1">{actionError}</span>
          <button onClick={() => setActionError(null)} className="shrink-0 text-red-400 hover:text-red-600 ml-2">✕</button>
        </div>
      )}

      {/* Page header */}
      <div className="flex items-center gap-4 px-6 pt-5 pb-4 border-b shrink-0" style={{ borderColor: theme.cardBorder }}>
        <button
          onClick={() => navigate('/tenants')}
          className="flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Departments
        </button>
        <span className="text-gray-300">/</span>
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-8 w-8 rounded-lg bg-orange-50 flex items-center justify-center shrink-0">
            <Building2 className="h-4 w-4 text-orange-500" />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-gray-900 truncate">{tenant.name}</h1>
            {tenant.slug && <p className="text-xs text-gray-400 font-mono">{tenant.slug}</p>}
          </div>
          {tenant.tier && (
            <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full capitalize shrink-0 ${TIER_COLORS[tenant.tier] || 'bg-gray-100 text-gray-600'}`}>
              {tenant.tier}
            </span>
          )}
          {tenant.status && (
            <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full capitalize shrink-0 ${STATUS_COLORS[tenant.status] || 'bg-gray-100 text-gray-600'}`}>
              {tenant.status}
            </span>
          )}
        </div>
        <div className="ml-auto flex items-center gap-2 shrink-0">
          <button
            onClick={handleEnterDepartment}
            disabled={enteringDept || tenant.status === 'suspended' || tenant.status === 'expired'}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50 transition-colors"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            {enteringDept ? 'Opening…' : 'Enter Department'}
          </button>
          <button
            onClick={handleEnterMobile}
            disabled={enteringMobile || tenant.status === 'suspended' || tenant.status === 'expired'}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <Smartphone className="h-3.5 w-3.5" />
            {enteringMobile ? 'Opening…' : 'Enter Mobile'}
          </button>
          <button
            onClick={() => setShowEditModal(true)}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Pencil className="h-3.5 w-3.5" /> Edit
          </button>
        </div>
      </div>

      {/* Full-width panel content */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <DepartmentPanel
          tenant={tenant}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          onSave={handleSave}
          onExtend={(tid, name) => setExtendTarget({ id: tid, name })}
          onConvert={(tid, name) => setConvertTarget({ id: tid, name })}
          onCleanup={(tid, name) => setCleanupTarget({ id: tid, name })}
          onMarkAsTrial={(tid, name) => setMarkTrialTarget({ id: tid, name })}
        />
      </div>

      {/* ── Extend Trial Modal ── */}
      {extendTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Extend Trial: {extendTarget.name}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Extension (days)</label>
                <input type="number" min="1" max="90" value={extendDays} onChange={(e) => setExtendDays(parseInt(e.target.value) || 14)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                <textarea rows={2} value={extendReason} onChange={(e) => setExtendReason(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Reason for extension..." />
              </div>
              <div className="flex justify-end gap-3">
                <button onClick={() => setExtendTarget(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
                <button onClick={handleExtend} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">Extend by {extendDays} days</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Convert Trial ── */}
      {convertTarget && (
        <ConfirmActionModal
          title="Convert Trial?"
          description={<>Convert <strong>{convertTarget.name}</strong> from trial to active subscription?</>}
          confirmButtonLabel="Yes, Convert"
          variant="warning"
          onConfirm={handleConvert}
          onClose={() => setConvertTarget(null)}
        />
      )}

      {/* ── Cleanup Trial ── */}
      {cleanupTarget && (
        <ConfirmActionModal
          title="Clean Up Trial?"
          description={<>Clean up expired trial <strong>{cleanupTarget.name}</strong>? This will suspend the tenant.</>}
          confirmButtonLabel="Yes, Clean Up"
          variant="danger"
          onConfirm={handleCleanup}
          onClose={() => setCleanupTarget(null)}
        />
      )}

      {/* ── Mark as Trial Modal ── */}
      {markTrialTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Mark as Trial: {markTrialTarget.name}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Trial Duration (days)</label>
                <input
                  type="number"
                  min="1"
                  max="90"
                  value={markTrialDays}
                  onChange={(e) => setMarkTrialDays(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                <textarea
                  value={markTrialReason}
                  onChange={(e) => setMarkTrialReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Why are you converting this to a trial?"
                />
              </div>
              <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                This will convert the active department to trial mode with the specified duration.
              </p>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setMarkTrialTarget(null)}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleMarkAsTrial}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Mark as Trial
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Department ── */}
      {showEditModal && tenant && (
        <EditDepartmentModal
          tenant={tenant}
          onClose={() => setShowEditModal(false)}
          onSaved={(updated) => {
            setTenant(updated);
            setShowEditModal(false);
          }}
        />
      )}
    </div>
  );
}
